import { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import flow from 'lodash/flow';
import get from 'lodash/get';
import compact from 'lodash/compact';
import classNames from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  logCategoryActionLabel,
  logClientInfo,
  logWebActivity,
} from 'src/common/actions/logging';
import { validateAndGetHexColor } from 'src/common/utils/color';
import cssVariables from 'src/common/styles/variables';
import connectWithExperiments from '../../../decorators/connectWithExperiments';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';

import isDesktop from '../../../utils/desktop/isDesktop';
import invokeDesktopFunction from '../../../utils/desktop/invokeDesktopFunction';
import { needsSignInDialog, needsSignUpDialog } from '../../../actions/auth';
import {
  fetchItemsAndOpenIntSelDialog,
  closeNowPlayingDialog,
} from '../../../actions/dialog';
import { getDestInfoUrlObj } from '../../../utils/guideItem/getGuideItemPathname';
import createSubscribeLink from '../../../utils/subscription/createSubscribeLink';
import { behaviors } from '../../../utils/guideItemTypes';
import { urlStringToComponents } from '../../../utils/urlHelpers';

import web from '../../../constants/analytics/categoryActionLabel/web';
import viewModel from '../../../constants/analytics/categoryActionLabel/viewModel';
import { WEB_ACTION_INTEREST_DIALOG_URL } from '../../../constants/dialogs/interestSelector';
import { UPSELL_CTA_TEXT } from '../../../constants/experiments/upsell';
import { textStyles, buttonStyles } from '../../../constants/styles';
import { desktopFuncNames } from '../../../constants/desktop';

import css from './action-button.module.scss';

const textCss = {
  [textStyles.Underline]: css.underlineText,
};

const buttonShapeStyles = {
  [buttonStyles.Rounded]: css.roundedButton,
};

const actionButtonLogging = {
  invalid: 'invalidButtonAction',
  unsupported: 'unsupportedButtonAction',
  exception: 'actionButtonException',
};

export class ActionButton extends Component {
  static propTypes = {
    button: PropTypes.object.isRequired,
    buttonActions: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    guideId: PropTypes.string,
    source: PropTypes.string,
    actionLabelPrefix: PropTypes.string.isRequired,
    getStyles: PropTypes.func.isRequired,
    isNowPlayingOpen: PropTypes.bool.isRequired,
    [WEB_ACTION_INTEREST_DIALOG_URL]: PropTypes.string,
    [UPSELL_CTA_TEXT]: PropTypes.string,
  };

  static contextType = LocationAndLocalizationContext;

  static defaultProps = {
    actionLabelPrefix: '',
    button: {},
  };

  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
  }

  handleSubscribe() {
    const {
      actions,
      guideId,
      source,
      actionLabelPrefix,
      history,
      isNowPlayingOpen,
    } = this.props;
    const { location } = this.context;

    const { buttonActions } = this.props;
    const sku1 = get(buttonActions, 'subscribe.product');
    const sku2 = get(buttonActions, 'subscribe.productSecondary');
    const additionalParams = { sku: compact([sku1, sku2]) };
    const subscribeLink = createSubscribeLink({
      source,
      guideId,
      location,
      additionalParams,
    });

    actions.logWebActivity(
      web.actions.tap,
      `${actionLabelPrefix}-${behaviors.subscribe}`,
    );
    if (isNowPlayingOpen) {
      actions.closeNowPlayingDialog();
    }
    history.push(subscribeLink);
  }

  handleBrowseProfileNavigation() {
    const { history, actions, actionLabelPrefix, button, isNowPlayingOpen } =
      this.props;
    const destinationInfo = get(button, 'destinationInfo', {});
    const browseUrlObj = getDestInfoUrlObj(destinationInfo);
    if (!browseUrlObj) {
      actions.logClientInfo({
        message: actionButtonLogging.invalid,
        context: { button },
      });
      return;
    }
    actions.logWebActivity(
      web.actions.tap,
      `${actionLabelPrefix}-${behaviors.browse}-${behaviors.profile}`,
      destinationInfo.id,
    );
    if (isNowPlayingOpen) {
      actions.closeNowPlayingDialog();
    }
    history.push(browseUrlObj);
  }

  openExternalLink() {
    const { actions, actionLabelPrefix, button } = this.props;
    const buttonUrl = get(button, 'data');
    actions.logWebActivity(
      web.actions.tap,
      `${actionLabelPrefix}-${behaviors.link}`,
      buttonUrl,
    );
    if (isDesktop()) {
      invokeDesktopFunction(desktopFuncNames.openSocialShare, buttonUrl);
    } else {
      window.open(buttonUrl);
    }
  }

  handleLinkAction() {
    const {
      button,
      actions,
      [WEB_ACTION_INTEREST_DIALOG_URL]: interestSelectorDialogUrl,
    } = this.props;

    const internalActions = {
      [interestSelectorDialogUrl]: actions.fetchItemsAndOpenIntSelDialog,
    };

    const buttonUrl = get(button, 'data', '');
    const { urlWithoutQueryParams, queryParams, queryString, pathname } =
      urlStringToComponents(buttonUrl, true);
    const internalAction = internalActions[urlWithoutQueryParams];

    if (internalAction) {
      internalAction(queryParams);
      actions.logCategoryActionLabel({
        category: viewModel.category,
        action: viewModel.actions.deeplink,
        label: `${pathname.slice(1)}${queryString}`, // slicing leading forward slash from pathname
      });
    } else {
      this.openExternalLink();
    }
  }

  handleClick() {
    const { button, onClose, actions, actionLabelPrefix } = this.props;

    const {
      openSignInDialog,
      openSignUpDialog,
      logClientInfo: logInfo,
    } = actions;

    const buttonAction = get(button, 'destination');
    try {
      // idea here is that we add more actions to this in the future
      switch (buttonAction) {
        case behaviors.dismiss: {
          onClose(`${actionLabelPrefix}-${behaviors.dismiss}`);
          break;
        }
        case behaviors.browse:
        case behaviors.profile: {
          this.handleBrowseProfileNavigation();
          break;
        }
        case behaviors.subscribe: {
          this.handleSubscribe();
          break;
        }
        case behaviors.signIn: {
          openSignInDialog();
          break;
        }
        case behaviors.signUp: {
          openSignUpDialog();
          break;
        }
        case behaviors.link: {
          this.handleLinkAction();
          break;
        }
        default: {
          // logging this for now until we add support for other types
          logInfo({
            message: actionButtonLogging.unsupported,
            context: { buttonAction },
          });
        }
      }
    } catch (ex) {
      logInfo({
        message: actionButtonLogging.exception,
        context: { buttonAction, message: get(ex, 'message') },
      });
    }
  }

  render() {
    const { button, getStyles, [UPSELL_CTA_TEXT]: upsellCtaText } = this.props;

    const { getLocalizedText } = this.context;

    const {
      shape,
      backgroundColor: bgColor,
      textColor,
      textStyle,
    } = getStyles(button.style);

    const backgroundColorToUse = validateAndGetHexColor(bgColor, 'transparent');
    const textColorToUse = validateAndGetHexColor(
      textColor,
      cssVariables['--secondary-color-5'],
    );
    const buttonAction = get(button, 'destination');

    const buttonTextStyle = {};
    if (textStyle === textStyles.Underline) {
      buttonTextStyle.borderColor = textColorToUse;
    }

    const buttonText =
      buttonAction === behaviors.subscribe && upsellCtaText
        ? getLocalizedText(upsellCtaText)
        : button.text;

    return (
      <div
        className={classNames(css.actionButton, buttonShapeStyles[shape])}
        onClick={this.handleClick}
        style={{ backgroundColor: backgroundColorToUse, color: textColorToUse }}
        data-testid={`actionButton-${buttonAction}`}
      >
        <div
          className={classNames(css.buttonText, textCss[textStyle])}
          style={buttonTextStyle}
          data-testid={`actionButtonText-${buttonAction}`}
        >
          {buttonText}
        </div>
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  const actions = {
    logWebActivity,
    logClientInfo,
    logCategoryActionLabel,
    fetchItemsAndOpenIntSelDialog,
    closeNowPlayingDialog,
    openSignInDialog: needsSignInDialog,
    openSignUpDialog: needsSignUpDialog,
  };

  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

function mapStateToProps(state) {
  return {
    isNowPlayingOpen: get(state, 'dialog.isNowPlayingDialogOpen', false),
  };
}

export default flow(
  connectWithExperiments([WEB_ACTION_INTEREST_DIALOG_URL, UPSELL_CTA_TEXT]),
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
)(ActionButton);
